import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { RedLockOverviewDto, RedLockOverviewMonthDto } from '@anessa/shared-models';
import { MonthLockService } from '../../../shared/month-lock/month-lock.service';
import { ProjectService } from '../../../shared/services/project/project.service';
import { formatAppDate } from '../../../shared/date/app-date-format';
import { MonthLockRowStatus, buildMonthLockStatus } from './month-lock-status';

/** Label and icon per month status — internal staff dialog (`~lockstatus`), so no i18n. */
const STATUS_LABELS: Record<MonthLockRowStatus, { label: string; icon: string }> = {
  open: { label: 'Open (current month)', icon: 'edit_calendar' },
  red_editable: { label: 'RED edit window', icon: 'edit_note' },
  closed_unlocked: { label: 'Closed, not locked yet', icon: 'lock_open' },
  locked_reviewed: { label: 'Locked, RED-reviewed (snapshot)', icon: 'lock' },
};

/**
 * `~lockstatus` dev command: the current plant's month lock — start month,
 * locked-through date, next lock, and the latest months with where each
 * stands — computed from the lock MonthLockService already holds, with the
 * same shared rules the server enforces.
 */
@Component({
  selector: 'app-month-lock-status-dialog',
  imports: [MatDialogModule, MatButtonModule, MatIconModule],
  templateUrl: './month-lock-status-dialog.html',
  styleUrl: './month-lock-status-dialog.scss',
})
export class MonthLockStatusDialog implements OnInit {
  private readonly monthLock = inject(MonthLockService);
  private readonly projectService = inject(ProjectService);

  readonly plantId = this.projectService.singlePlantId ?? null;
  readonly plantName = this.projectService.currentProject()?.plantMeta?.name ?? this.plantId;

  readonly status = computed(() => {
    const lock = this.monthLock.lock();
    return lock && this.plantId
      ? buildMonthLockStatus(lock, this.plantId, this.monthLock.myUnlock())
      : null;
  });

  /**
   * What only the server knows: every open unlock on the plant and each
   * reviewed month's version and pending correction. Null until it loads, or
   * when it fails (errors are reported by the api-error interceptor).
   */
  readonly overview = signal<RedLockOverviewDto | null>(null);
  private readonly versionsByMonth = computed(
    () => new Map((this.overview()?.months ?? []).map((m) => [m.month, m])),
  );

  ngOnInit(): void {
    if (!this.plantId) return;
    this.monthLock.overview(this.plantId).subscribe({
      next: (overview) => this.overview.set(overview),
      error: () => undefined,
    });
  }

  version(month: string): RedLockOverviewMonthDto | null {
    return this.versionsByMonth().get(month) ?? null;
  }

  time(iso: string): string {
    return formatAppDate(iso, 'datetime');
  }

  label(status: MonthLockRowStatus): string {
    return STATUS_LABELS[status].label;
  }

  icon(status: MonthLockRowStatus): string {
    return STATUS_LABELS[status].icon;
  }
}
